import { useMemo, useState } from 'react';

import { type ColumnDef } from '@tanstack/react-table';

import {
  DataTable,
  DataTableColumnsMenu,
  DataTableInteractiveArea,
} from '@spotify-internal/data-table';
import { Flex, Text } from '@spotify-internal/encore-web';
import { useLabelsForResource } from '@spotify/confidence-common-react';
import {
  ConfirmDialog,
  FilterRow,
  FilterSearch,
  ListEmptyState,
} from '@spotify/confidence-core-react';
import type { CollectionEntryFragment } from '@spotify/confidence-graphql';
import {
  DataTableInfiniteScroll,
  useDataTableColumnVisibility,
  useResourceDataTable,
} from '@spotify/confidence-resource-data-table-react';

import {
  type CollectionEntryExtensionOptions,
  findCollectionEntryExtension,
  getExtensionDisplayName,
} from '../../extensions';
import { useDeleteCollectionEntry } from '../../hooks';
import {
  type CollectionEntryTableRow,
  type EntryToRemove,
  createActionsColumn,
  createAddedColumn,
  createExtensionColumn,
  createFavoriteColumn,
  createLabelsColumn,
  createNameColumn,
  createOwnerColumn,
  createTypeColumn,
} from './collectionEntryColumns';

import styles from './CollectionEntriesTable.module.css';

type CollectionEntriesTableProps = {
  filteredEntries: CollectionEntryFragment[];
  extensions: CollectionEntryExtensionOptions[];
  activeExtension?: CollectionEntryExtensionOptions;
  scrollRef: React.RefObject<HTMLElement>;
  scrollElement: HTMLElement | null;
  isLoading: boolean;
  hasEntries: boolean;
  hasNextPage: boolean;
  hasActiveFilters: boolean;
  error: Error | undefined;
  onLoadMore: () => Promise<unknown>;
};

export const CollectionEntriesTable = ({
  filteredEntries,
  extensions,
  activeExtension,
  scrollRef,
  scrollElement,
  isLoading,
  hasEntries,
  hasNextPage,
  hasActiveFilters,
  error,
  onLoadMore,
}: CollectionEntriesTableProps) => {
  const [entryToRemove, setEntryToRemove] = useState<EntryToRemove | null>(
    null,
  );
  const { labelDefinitions } = useLabelsForResource({});
  const { deleteCollectionEntry } = useDeleteCollectionEntry();

  const rows = useMemo<CollectionEntryTableRow[]>(
    () =>
      filteredEntries.map(entry => {
        const extension = findCollectionEntryExtension(extensions, entry);
        const resource = entry.resource ?? undefined;
        const displayName = resource
          ? getExtensionDisplayName(extension, resource, entry)
          : entry.name;
        const resourceName =
          resource && 'name' in resource ? String(resource.name) : entry.name;
        return { name: resourceName, displayName, entry, extension, resource };
      }),
    [filteredEntries, extensions],
  );

  const extensionColumns = activeExtension?.columns;

  const columns = useMemo<
    ColumnDef<CollectionEntryTableRow, CollectionEntryTableRow>[]
  >(
    () => [
      createFavoriteColumn(),
      createNameColumn(),
      createTypeColumn(),
      ...(extensionColumns?.map(createExtensionColumn) ?? []),
      createOwnerColumn(),
      createLabelsColumn(labelDefinitions),
      createAddedColumn(),
      createActionsColumn(setEntryToRemove),
    ],
    [extensionColumns, labelDefinitions],
  );

  const { columnVisibility, onColumnVisibilityChange } =
    useDataTableColumnVisibility({
      storageKey: 'collection-entries-columns',
    });

  const showEmptyState =
    rows.length === 0 &&
    hasActiveFilters &&
    !hasNextPage &&
    !isLoading &&
    !error;

  const { table, interactionProps } =
    useResourceDataTable<CollectionEntryTableRow>({
      columns,
      data: rows,
      columnVisibility,
      onColumnVisibilityChange,
      externalScrollRef: scrollRef,
    });

  return (
    <>
      <Flex className={styles.filterBar} alignItems="start" gap="tighter">
        <div className={styles.filterRowWrapper}>
          <FilterRow>
            <div className={styles.searchField}>
              <FilterSearch />
            </div>
          </FilterRow>
        </div>
        <DataTableColumnsMenu instance={table} />
      </Flex>
      <DataTableInteractiveArea {...interactionProps}>
        <div className={styles.table}>
          <DataTable
            instance={table}
            aria-label="Collection entries"
            isLoading={isLoading && !hasEntries}
          />
        </div>
      </DataTableInteractiveArea>
      {showEmptyState && (
        <ListEmptyState
          title="No matching entries"
          description="Try a different search or filter."
        />
      )}
      <DataTableInfiniteScroll
        root={scrollElement}
        itemCount={rows.length}
        hasNextPage={hasNextPage}
        loading={isLoading && hasEntries}
        onLoadMore={onLoadMore}
      >
        Loading more entries…
      </DataTableInfiniteScroll>
      {entryToRemove && (
        <ConfirmDialog
          title="Remove from collection"
          action="Remove"
          onClose={() => setEntryToRemove(null)}
          onConfirm={() => deleteCollectionEntry(entryToRemove.name)}
         data-track-source-anchor="887d580cf1;collection-entries-table-confirm-dialog">
          <Text variant="bodyMedium">
            Are you sure you want to remove{' '}
            <strong>{entryToRemove.displayName}</strong> from this collection?
          </Text>
        </ConfirmDialog>
      )}
    </>
  );
};
